import type { ILogger, LoggerConfig, LogContext, LogLevel } from "./_types";

// Extend the Window interface to include logger and other controls
declare global {
  interface Window {
    logger?: ILogger;
    setLogMode?: (mode: keyof typeof LOG_MODES) => Promise<void>;
    debugMode?: () => Promise<void>;
    logOff?: () => Promise<void>;
    logConfig?: () => LoggerConfig;
  }
}

import {
  DEFAULT_CONFIG,
  LOG_LEVELS,
  COLORS,
  LEVEL_EMOJIS,
  LOG_MODES,
  getStoredConfig,
  saveConfigToStorage,
} from "./_config";

import { StorageHelper } from "~/common/helpers";

/**
 * Logger Implementation
 */
class Logger implements ILogger {
  constructor(initialConfig: Partial<LoggerConfig> = {}) {
    this.initializeStorage(initialConfig);
    this.setupGlobalControls();
  }

  /**
   * Initialize storage with default config if needed
   */
  private async initializeStorage(
    initialConfig: Partial<LoggerConfig>
  ): Promise<void> {
    try {
      const storedConfig = await getStoredConfig();
      if (Object.keys(storedConfig).length === 0) {
        // No config in storage, save initial config
        const config = { ...DEFAULT_CONFIG, ...initialConfig };
        await saveConfigToStorage(config);
      }
    } catch (error) {
      console.warn("Failed to initialize logger storage:", error);
    }
  }

  /**
   * Setup global window controls for easy debugging
   */
  private setupGlobalControls(): void {
    if (typeof window !== "undefined") {
      // Make logger accessible globally
      window.logger = this;

      // Setup easy mode switching
      window.setLogMode = async (mode: keyof typeof LOG_MODES) => {
        if (LOG_MODES[mode]) {
          await this.setConfig(LOG_MODES[mode]);
          console.log(
            `🎯 Logger mode set to: ${mode}`,
            this.getCurrentConfig()
          );
        } else {
          console.warn(
            `❌ Invalid log mode: ${mode}. Available: ${Object.keys(
              LOG_MODES
            ).join(", ")}`
          );
        }
      };

      // Quick debug mode
      window.debugMode = () => this.setConfig({ level: "debug" });
      window.logOff = () => this.setConfig({ level: "off" });
      window.logConfig = () => this.getCurrentConfig();
    }
  }

  /**
   * Get current config from storage synchronously with fallback
   */
  private getCurrentConfig(): LoggerConfig {
    try {
      // Try to get from storage synchronously - using raw localStorage to avoid circular dependency
      const storedConfig = JSON.parse(
        localStorage.getItem(StorageHelper.KEY.LOGGER_CONFIG) || "{}"
      );
      return { ...DEFAULT_CONFIG, ...storedConfig };
    } catch {
      return DEFAULT_CONFIG;
    }
  }

  /**
   * Check if a log level is enabled
   */
  public isLevelEnabled(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.getCurrentConfig().level];
  }

  /**
   * Format timestamp
   */
  private formatTimestamp(): string {
    return new Date().toISOString().split("T")[1].slice(0, -1);
  }

  /**
   * Format log message with styling
   */
  private formatMessage(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): { message: string; styles: string[] } {
    const config = this.getCurrentConfig();
    const parts: string[] = [];
    const styles: string[] = [];

    // Add prefix
    if (config.prefix) {
      parts.push(`${config.prefix}`);
    }

    // Add emoji
    const emoji = LEVEL_EMOJIS[level as keyof typeof LEVEL_EMOJIS];
    if (emoji) {
      parts.push(emoji);
    }

    // Add timestamp
    if (config.timestamps) {
      parts.push(`%c[${this.formatTimestamp()}]`);
      styles.push(`color: ${COLORS.timestamp}; font-weight: normal;`);
    }

    // Add component
    if (config.showComponent && context?.component) {
      parts.push(`%c[${context.component}]`);
      styles.push(`color: ${COLORS.component}; font-weight: bold;`);
    }

    // Add file
    if (config.showFile && context?.file) {
      parts.push(`%c{${context.file}}`);
      styles.push(`color: ${COLORS.file}; font-style: italic;`);
    }

    // Add function
    if (context?.function) {
      parts.push(`%c${context.function}:`);
      const color =
        level !== "off" && COLORS[level as keyof typeof COLORS]
          ? COLORS[level as keyof typeof COLORS]
          : "#000000";
      styles.push(`color: ${color}; font-weight: bold;`);
    }

    // Add main message with level color
    parts.push(`%c${message}`);
    const messageColor =
      level !== "off" && COLORS[level as keyof typeof COLORS]
        ? COLORS[level as keyof typeof COLORS]
        : "#000000";
    styles.push(
      `color: ${messageColor}; font-weight: ${
        level === "error" ? "bold" : "normal"
      };`
    );

    return {
      message: parts.join(" "),
      styles,
    };
  }

  /**
   * Log a message at specified level
   */
  private log(
    level: LogLevel,
    message: string,
    data?: any,
    context?: LogContext
  ): void {
    if (!this.isLevelEnabled(level)) {
      return;
    }

    const config = this.getCurrentConfig();
    const { message: formattedMessage, styles } = this.formatMessage(
      level,
      message,
      context
    );

    // Choose console method based on level
    const consoleMethod =
      level === "debug" ? "log" : level === "off" ? "log" : level;

    if (config.colors && styles.length > 0) {
      console[consoleMethod](formattedMessage, ...styles);
    } else {
      // Fallback without colors
      const plainMessage = formattedMessage.replace(/%c/g, "");
      console[consoleMethod](plainMessage);
    }

    // Log data separately if provided
    if (data !== undefined) {
      console[consoleMethod]("  📄 Data:", data);
    }

    // Log metadata if provided
    if (context?.metadata && Object.keys(context.metadata).length > 0) {
      console[consoleMethod]("  🏷️ Metadata:", context.metadata);
    }
  }

  /**
   * Debug level logging
   */
  public debug(message: string, data?: any, context?: LogContext): void {
    this.log("debug", message, data, context);
  }

  /**
   * Info level logging
   */
  public info(message: string, data?: any, context?: LogContext): void {
    this.log("info", message, data, context);
  }

  /**
   * Warning level logging
   */
  public warn(message: string, data?: any, context?: LogContext): void {
    this.log("warn", message, data, context);
  }

  /**
   * Error level logging
   */
  public error(message: string, data?: any, context?: LogContext): void {
    this.log("error", message, data, context);
  }

  /**
   * Update logger configuration
   */
  public async setConfig(config: Partial<LoggerConfig>): Promise<void> {
    try {
      const currentConfig = this.getCurrentConfig();
      const newConfig = { ...currentConfig, ...config };
      await saveConfigToStorage(newConfig);
    } catch (error) {
      console.warn("Failed to save logger config to storage:", error);
    }
  }

  /**
   * Get current configuration
   */
  public getConfig(): LoggerConfig {
    return this.getCurrentConfig();
  }

  /**
   * Create a child logger with specific context
   */
  public createChild(defaultContext: LogContext): ILogger {
    const childLogger = {
      debug: (message: string, data?: any, context?: LogContext) =>
        this.debug(message, data, { ...defaultContext, ...context }),
      info: (message: string, data?: any, context?: LogContext) =>
        this.info(message, data, { ...defaultContext, ...context }),
      warn: (message: string, data?: any, context?: LogContext) =>
        this.warn(message, data, { ...defaultContext, ...context }),
      error: (message: string, data?: any, context?: LogContext) =>
        this.error(message, data, { ...defaultContext, ...context }),
      isLevelEnabled: this.isLevelEnabled.bind(this),
      setConfig: this.setConfig.bind(this),
      getConfig: this.getConfig.bind(this),
    };

    return childLogger;
  }
}

// Create and export global logger instance
export const logger = new Logger();

// Export the Logger class for custom instances
export { Logger };
